import { SaleTerm } from 'constants/payments';
import type {
  MemberOfferQuote,
  MemberOfferSubscription,
  MemberOfferTerm,
} from 'constants/payloads/memberOffers';
import { memberMoney } from 'components/MemberOffers/format';
import { getMemberSaleLastDay } from 'constants/memberOffers';
import { formatDiscountPercent } from 'utils/shared/payments/getResubscribeOfferState';
import {
  DASHBOARD_REGULAR_ANNUAL_CENTS,
  DASHBOARD_REGULAR_MONTHLY_CENTS,
  DASHBOARD_REGULAR_QUARTERLY_CENTS,
  describeMonths,
  percentOff,
} from 'screens/CheckoutUpgradeFunnel/dashboardFunnelPricing';
import {
  DASHBOARD_ANNUAL_INCLUDED_LINES,
  DASHBOARD_ANNUAL_OFFER_QUOTE,
  DASHBOARD_LIFETIME_INCLUDED_LINES,
  DASHBOARD_LIFETIME_OFFER_QUOTE,
  DashboardOfferLine,
} from 'screens/CheckoutUpgradeFunnel/dashboardOfferContent';

/**
 * Every Dashboard member-offer sentence, built from the signed quote so it can be asserted without a
 * DOM. The modal renders it through the post-checkout funnel's own components, so the in-app offer
 * argues the way the funnel does: the card decides, the numbers carry the argument, the story proves.
 *
 * The rules, all of them Rob's: no em dash, no hedge, no methodology, no block of text. "Lock in"
 * and "forever" only where the discount is on every renewal; a once discount says "this year". A
 * deadline is only ever a real one. The everyday offer has no clock, so it has no scarcity line and
 * no decline dialog. Technical Analysis gets no member offers; it keeps its Settings dialog.
 */

export interface CopyRun {
  text: string;
  strong?: boolean;
  /** Kept on one line where it fits: a narrow screen breaks the line before it, not inside it. */
  whole?: boolean;
}
export type CopyLine = CopyRun[];

export type MemberOfferVariant =
  | 'annual-everyday'
  | 'annual-forever'
  | 'annual-kept'
  | 'annual-sale-forever'
  | 'annual-sale-limited'
  | 'annual-email'
  | 'annual-email-kept'
  | 'lifetime'
  | 'lifetime-sale';

export interface MemberOfferStat {
  value: string;
  label: string;
}

export interface MemberOfferReceiptRow {
  label: string;
  detail?: string;
  value: string;
  status?: 'paid' | 'today';
}

export interface MemberOfferLedgerBar {
  amountCents: number;
  label: string;
  value: string;
  isYours?: boolean;
}

export interface MemberOfferDialogCopy {
  title: string;
  body: string;
  keepLabel: string;
  leaveLabel: string;
}

export interface MemberOfferCopy {
  variant: MemberOfferVariant;
  card: {
    eyebrow: string;
    headline: string;
    price: { now: string; unit?: string; was: string; wasLabel: string };
    priceCaption: CopyLine;
    stats: MemberOfferStat[];
    urgency: string;
    checkboxLabel: string;
    chargeLine: CopyLine;
    ctaLabel: string;
    ctaSubmittingLabel: string;
    declineLabel: string;
  };
  receipt: {
    title: string;
    rows: MemberOfferReceiptRow[];
    total: { label: string; value: string };
    footnote: CopyLine;
  };
  ledger: {
    title: string;
    tiles: Array<{ value: string; label: CopyLine }>;
    bars: MemberOfferLedgerBar[];
    barsLabel: string;
  };
  included: { title: string; lines: DashboardOfferLine[] };
  proof: { author: string; text: string };
  bar: { price: string; detail: string; cta: string; decline: string };
  success: { title: string; body: string };
  /** The Before-you-go step behind "No thanks". Only an offer with a real end date has one. */
  dialog: MemberOfferDialogCopy | null;
}

type SourceTerm = SaleTerm.Monthly | SaleTerm.Quarterly | SaleTerm.Annual;

const PERIODS: Record<SourceTerm, number> = {
  [SaleTerm.Monthly]: 12,
  [SaleTerm.Quarterly]: 4,
  [SaleTerm.Annual]: 1,
};
const ADJECTIVE: Record<SourceTerm, string> = {
  [SaleTerm.Monthly]: 'monthly',
  [SaleTerm.Quarterly]: 'quarterly',
  [SaleTerm.Annual]: 'annual',
};
const UNIT: Record<SourceTerm, string> = {
  [SaleTerm.Monthly]: 'month',
  [SaleTerm.Quarterly]: 'quarter',
  [SaleTerm.Annual]: 'year',
};
const SHORT_UNIT: Record<SourceTerm, string> = {
  [SaleTerm.Monthly]: '/mo',
  [SaleTerm.Quarterly]: '/qtr',
  [SaleTerm.Annual]: '/yr',
};

const sourceTermOf = (term: SaleTerm): SourceTerm =>
  term === SaleTerm.Quarterly || term === SaleTerm.Annual ? term : SaleTerm.Monthly;

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
const strong = (text: string): CopyRun => ({ text, strong: true });
const plain = (text: string): CopyRun => ({ text });
const whole = (text: string): CopyRun => ({ text, whole: true });

/** "1st", "2nd", "3rd", "4th": the renewal by which Lifetime has paid for itself. */
export const ordinal = (count: number) => {
  const rest = count % 100;
  if (rest >= 11 && rest <= 13) return `${count}th`;
  const suffix = ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[count % 10] ?? 'th';
  return `${count}${suffix}`;
};

/** A sale's last day, in Pacific time: see `getMemberSaleLastDay`. */
export const memberDeadline = getMemberSaleLastDay;

/** The day a renewal charges. */
export const memberRenewalDay = (at: number) =>
  new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export const getMemberOfferVariant = (
  quote: MemberOfferQuote,
  fromEmail = false,
): MemberOfferVariant => {
  if (quote.term === SaleTerm.Lifetime) return quote.lifetimeSale ? 'lifetime-sale' : 'lifetime';
  const discount = quote.annualDiscount;
  const kept = !!discount?.keepsExisting;
  if (fromEmail || quote.offerCode) return kept ? 'annual-email-kept' : 'annual-email';
  if (quote.sale && !kept)
    return quote.sale.duration === 'forever' ? 'annual-sale-forever' : 'annual-sale-limited';
  if (discount?.duration === 'forever') return kept ? 'annual-kept' : 'annual-forever';
  return 'annual-everyday';
};

/** What the member pays now, from the quote: per period, per month and per year. */
const currentRate = (quote: MemberOfferQuote, term: SourceTerm) => {
  const annualCents = quote.currentAnnualCents;
  return {
    annualCents,
    periodCents: Math.round(annualCents / PERIODS[term]),
    perMonthCents: Math.round(annualCents / 12),
    exactPerMonthCents: annualCents / 12,
  };
};

const dialogLeave = (money: (cents: number) => string, periodCents: number, term: SourceTerm) =>
  `Back to trading at ${money(periodCents)}/${UNIT[term]}`;

const getAnnualCopy = (
  quote: MemberOfferQuote,
  term: SourceTerm,
  variant: MemberOfferVariant,
): MemberOfferCopy => {
  const money = (cents: number) => memberMoney(cents, quote.currency);
  const current = currentRate(quote, term);
  const adjective = ADJECTIVE[term];
  const unit = UNIT[term];
  const first = quote.firstTermCents;
  const renewal = quote.renewalCents;
  const list = quote.annualListCents ?? renewal;
  const due = quote.amountDueCents;
  const forever = quote.annualDiscount?.duration === 'forever';
  const percent = quote.annualDiscount?.percentOff ?? null;
  const off =
    percent !== null ? `${formatDiscountPercent(percent)}% off` : `${money(list - first)} off`;
  const savedFirst = current.annualCents - first;
  const savedAfter = current.annualCents - renewal;
  const pctFirst = percentOff(current.annualCents, first);
  const fiveYear = current.annualCents * 5 - (first + renewal * 4);
  const monthsFree = current.exactPerMonthCents > 0 ? savedFirst / current.exactPerMonthCents : 0;
  const firstPerMonth = money(Math.round(first / 12));
  const currentPeriod = `${money(current.periodCents)}/${unit}`;
  const deadline = quote.sale ? memberDeadline(quote.sale.endsAt) : '';
  const wasLabel = quote.currentDiscountEndsAt ? 'your regular rate' : 'what you’re paying now';

  const eyebrow: Record<string, string> = {
    'annual-everyday': `Annual · ${off} your first year`,
    'annual-forever': `Annual · ${off}, forever`,
    'annual-kept':
      percent !== null
        ? `Annual · your ${formatDiscountPercent(percent)}% off comes with you`
        : 'Annual · your discount comes with you',
    'annual-sale-forever': `Member sale · ${off}, forever`,
    'annual-sale-limited': `Member sale · ${off} your first year`,
    'annual-email': `Your email offer · ${off}, forever`,
    'annual-email-kept':
      percent !== null
        ? `Your email offer · your ${formatDiscountPercent(percent)}% off stays`
        : 'Your email offer · your discount stays',
  };
  const temporary = !!quote.currentDiscountEndsAt;
  const savesFirst = temporary
    ? `Save ${money(savedFirst)} on your regular rate.`
    : `Save ${money(savedFirst)} this year.`;
  const headline: Record<string, string> = {
    'annual-everyday': `Go annual. ${savesFirst}`,
    'annual-forever': `Lock in ${off}. Forever.`,
    'annual-kept': `Keep your ${off}. Forever.`,
    'annual-sale-forever': `Lock in ${off}. Forever.`,
    'annual-sale-limited': `Go annual. ${savesFirst}`,
    'annual-email': `Lock in ${off}. Forever.`,
    'annual-email-kept': `Keep your ${off}. Forever.`,
  };
  const discountEnds = temporary
    ? `Your discount ends ${memberRenewalDay(
        quote.currentDiscountEndsAt!,
      )}. Your annual price holds all year.`
    : '';
  const urgency: Record<string, string> = {
    'annual-everyday':
      discountEnds || `Staying ${adjective} costs you ${money(savedFirst)} more this year.`,
    'annual-forever':
      discountEnds || `Staying ${adjective} costs you ${money(savedFirst)} more every year.`,
    'annual-kept':
      discountEnds || `Staying ${adjective} costs you ${money(savedFirst)} more every year.`,
    'annual-sale-forever': `The sale ends ${deadline}. Lock it in before the clock runs out.`,
    'annual-sale-limited': `The sale ends ${deadline}. Grab it before the clock runs out.`,
    'annual-email': 'Your 7-day email offer. Lock it in and it’s yours for life.',
    'annual-email-kept':
      percent !== null
        ? `Your ${formatDiscountPercent(percent)}% off beats the email offer, so it comes with you.`
        : 'Your discount beats the email offer, so it comes with you.',
  };

  const credit = quote.creditCents;
  const tax = quote.taxCents;
  const renewsLine = ` Renews at ${money(renewal)} a year.`;
  const chargeLine: CopyLine = [
    ...(credit > 0
      ? [plain(`Your ${money(credit)} of unused ${adjective} time is credited. `)]
      : []),
    strong(tax > 0 ? `Pay ${money(due)} today, tax included.` : `Pay ${money(due)} today.`),
    plain(renewsLine),
  ];

  const rows: MemberOfferReceiptRow[] = [
    forever
      ? {
          label: 'Your annual price',
          detail: `${capitalize(off)} our ${money(list)} price, every renewal`,
          value: money(first),
        }
      : {
          label: 'Your first year',
          detail: `Annual, ${off} our ${money(list)} price`,
          value: money(first),
        },
    ...(credit > 0
      ? [
          {
            label: `Unused ${adjective} time`,
            detail: 'Credited to your new year',
            value: `−${money(credit)}`,
          },
        ]
      : []),
    ...(tax !== 0 ? [{ label: 'Tax', value: money(tax) }] : []),
    ...(quote.adjustmentCents < 0
      ? [
          {
            label: 'Account credit',
            detail: 'Already on your account',
            value: `−${money(-quote.adjustmentCents)}`,
          },
        ]
      : quote.adjustmentCents > 0
      ? [
          {
            label: 'Account balance',
            detail: 'Owed on your account',
            value: money(quote.adjustmentCents),
          },
        ]
      : []),
  ];

  const monthsTile =
    monthsFree >= 1
      ? {
          value: `${Math.round(monthsFree)} mo`,
          label: [
            strong(capitalize(describeMonths(monthsFree))),
            plain(
              temporary
                ? ' free against your regular rate'
                : forever
                ? ' of the Dashboard free, every year'
                : ' of the Dashboard free this year',
            ),
          ],
        }
      : {
          value: money(Math.round(savedFirst / 12)),
          label: [plain('back in your pocket '), strong('every month')],
        };
  const tiles = forever
    ? [
        {
          value: `−${pctFirst}%`,
          label: [
            plain(temporary ? 'vs your regular ' : 'vs paying '),
            strong(`${money(current.perMonthCents)} a month`),
          ],
        },
        {
          value: off,
          label: [plain('our '), strong(money(list)), plain(' annual price, forever')],
        },
        monthsTile,
        {
          value: money(fiveYear),
          label: [
            plain('kept over '),
            strong('five years'),
            ...(temporary ? [plain(' at your regular rate')] : []),
          ],
        },
      ]
    : [
        {
          value: `−${pctFirst}%`,
          label: temporary
            ? [plain('vs your regular '), strong(`${money(current.perMonthCents)} a month`)]
            : [
                plain('vs paying '),
                strong(`${money(current.perMonthCents)} a month`),
                plain(' this year'),
              ],
        },
        {
          value: money(savedAfter),
          label: [plain('back every year after, at our '), strong(money(list)), plain(' annual')],
        },
        monthsTile,
        {
          value: money(fiveYear),
          label: [
            plain('kept over '),
            strong('five years'),
            ...(temporary ? [plain(' at your regular rate')] : []),
          ],
        },
      ];
  const bars: MemberOfferLedgerBar[] = [
    {
      amountCents: current.annualCents,
      label: quote.currentDiscountEndsAt
        ? `Your ${adjective} plan at its regular rate`
        : `Your ${adjective} plan for a year`,
      value: money(current.annualCents),
    },
    ...(list > first && list < current.annualCents
      ? [{ amountCents: list, label: 'Our annual price', value: money(list) }]
      : []),
    {
      amountCents: first,
      isYours: true,
      label: forever ? 'Your price, every year' : 'Your first year',
      value: money(first),
    },
  ];

  const footnote = forever
    ? `Starts today. Renews at ${money(
        renewal,
      )} a year, ${off} every year, plus any tax. Cancel any time in Settings.`
    : `Starts today. Renews at ${money(
        renewal,
      )} a year, plus any tax. Cancel any time in Settings.`;

  const lockedTick = `Yes! Lock in ${off} for life. ${money(due)} today, then ${money(
    renewal,
  )} a year.`;
  const switchTick =
    savedFirst > 0 && !temporary
      ? `Yes! Save ${money(savedFirst)} on annual this year. ${money(due)} today, then ${money(
          renewal,
        )} a year.`
      : `Yes! Go annual at ${money(first)} for my first year. ${money(due)} today, then ${money(
          renewal,
        )} a year.`;
  const foreverCta = `Lock in ${money(first)}/year`;
  const onceCta = `Go annual for ${money(first)}`;

  const staysBody = temporary
    ? `Your discount ends ${memberRenewalDay(
        quote.currentDiscountEndsAt!,
      )}. Then it’s ${currentPeriod} again for the exact same Dashboard. The sale ends ${deadline}.`
    : null;
  const leaveLabel = temporary
    ? `Back to trading on ${adjective}`
    : dialogLeave(money, current.periodCents, term);
  const dialog: MemberOfferDialogCopy | null =
    variant === 'annual-sale-forever'
      ? {
          title: `Leave without ${off} for life?`,
          body:
            staysBody ||
            `You’ll keep paying ${currentPeriod}. That’s ${money(
              savedFirst,
            )} more every year for the exact same Dashboard. The sale ends ${deadline}.`,
          keepLabel: foreverCta,
          leaveLabel,
        }
      : variant === 'annual-sale-limited'
      ? {
          title: 'Leave without the sale price?',
          body:
            staysBody ||
            `You’ll keep paying ${currentPeriod}. That’s ${money(
              savedFirst,
            )} more this year for the exact same Dashboard. The sale ends ${deadline}.`,
          keepLabel: onceCta,
          leaveLabel,
        }
      : null;

  return {
    variant,
    card: {
      eyebrow: eyebrow[variant],
      headline: headline[variant],
      price: {
        now: firstPerMonth,
        unit: '/mo',
        was: `${money(current.perMonthCents)}/mo`,
        wasLabel,
      },
      priceCaption: forever
        ? [
            strong(`${money(first)} a year, ${off} every renewal.`),
            plain(' For as long as you stay. The exact same Dashboard.'),
          ]
        : [
            strong(`${money(first)} for your whole first year.`),
            plain(` Then ${money(renewal)} a year. The exact same Dashboard.`),
          ],
      stats: forever
        ? [
            {
              value: `−${pctFirst}%`,
              label: temporary ? `vs your regular ${adjective} rate` : `vs paying ${adjective}`,
            },
            {
              value: money(savedFirst),
              label: temporary
                ? 'a year back on your regular rate'
                : 'back in your pocket every year',
            },
            { value: off, label: `our ${money(list)} annual, forever` },
          ]
        : [
            {
              value: `−${pctFirst}%`,
              label: temporary
                ? `vs your regular ${adjective} rate`
                : `this year vs paying ${adjective}`,
            },
            { value: money(savedAfter), label: 'back in your pocket every year after' },
            { value: off, label: `your first year of our ${money(list)} annual` },
          ],
      urgency: urgency[variant],
      checkboxLabel: forever ? lockedTick : switchTick,
      chargeLine,
      ctaLabel: forever ? foreverCta : onceCta,
      ctaSubmittingLabel: forever ? 'Locking in your rate…' : 'Switching you to annual…',
      declineLabel: temporary
        ? `No thanks, I’ll stay ${adjective}`
        : `No thanks, I’ll keep paying ${currentPeriod}`,
    },
    receipt: {
      title: 'One payment. Your whole year starts now.',
      rows,
      total: { label: 'Due today', value: money(due) },
      footnote: [plain(footnote)],
    },
    ledger: {
      title: temporary
        ? `${money(savedFirst)} a year less than your regular rate.`
        : forever
        ? `${money(savedFirst)} stays in your pocket. Every year.`
        : `${money(savedFirst)} stays in your pocket this year.`,
      tiles,
      bars,
      barsLabel: 'One year of the Dashboard, compared',
    },
    included: {
      title: 'Everything. Including what we haven’t built yet.',
      lines: forever
        ? DASHBOARD_ANNUAL_INCLUDED_LINES
        : [
            { lead: 'Every feature we ship,', rest: 'included in your plan.' },
            { lead: 'Even features we add to higher tiers', rest: 'later.' },
            { lead: 'We ship every week.', rest: 'New features never cost extra.' },
          ],
    },
    proof: DASHBOARD_ANNUAL_OFFER_QUOTE,
    bar: {
      price: `${firstPerMonth}/mo`,
      detail: forever ? `${off}, forever` : 'your first year',
      cta: forever ? 'Lock it in' : 'Go annual',
      decline: 'No thanks',
    },
    success: forever
      ? {
          title: `Your ${off} is locked in.`,
          body: `${money(savedFirst)} ${
            temporary ? 'a year less than your regular rate' : 'stays in your pocket every year'
          }. Your plan renews at ${money(renewal)} a year from today, ${off} every time.`,
        }
      : {
          title: 'You’re on annual.',
          body: `${money(savedFirst)} ${
            temporary ? 'less than a year at your regular rate' : 'stays in your pocket this year'
          }, and ${money(savedAfter)} every year after. Your plan renews at ${money(
            renewal,
          )} a year from today.`,
        },
    dialog,
  };
};

const getLifetimeCopy = (
  quote: MemberOfferQuote,
  term: SourceTerm,
  variant: MemberOfferVariant,
): MemberOfferCopy => {
  const money = (cents: number) => memberMoney(cents, quote.currency);
  const current = currentRate(quote, term);
  const adjective = ADJECTIVE[term];
  const unit = UNIT[term];
  const price = quote.firstTermCents;
  const due = quote.amountDueCents;
  const sale = quote.lifetimeSale || null;
  const list = sale?.listCents ?? quote.lifetimeListCents ?? price;
  const saleOff = list - price;
  const deadline = sale ? memberDeadline(sale.endsAt) : '';
  const payment = term === SaleTerm.Annual ? 'renewal' : 'payment';
  // A temporary discount makes the next payment smaller than the regular rate, so a named date and
  // amount would overstate it. Those lines speak to the regular rate instead.
  const temporary = !!quote.currentDiscountEndsAt;
  const periodEnds = temporary ? null : quote.currentPeriodEndsAt || null;
  const regular = temporary ? ' at your regular rate' : '';
  const currentPeriod = `${money(current.periodCents)}/${unit}`;
  const fiveYear = current.annualCents * 5 - price;

  // The quote gives the regular rate, not the remaining discounted payments. A temporary
  // discount therefore sells the single payment, without inventing a payback date.
  const breakEven = temporary
    ? {
        value: '1',
        label: 'payment. Yours for life',
        tile: [plain('payment. '), strong('Yours for life')],
      }
    : term === SaleTerm.Annual
    ? {
        value: ordinal(Math.max(1, Math.ceil(price / Math.max(1, current.annualCents)))),
        label: 'renewal and Lifetime has paid for itself',
        tile: [plain('renewal and Lifetime has '), strong('paid for itself')],
      }
    : (() => {
        const years = price / Math.max(1, current.annualCents);
        const value = Number.isInteger(years) ? String(years) : `Under ${Math.ceil(years)}`;
        return {
          value,
          label: 'years and Lifetime has paid for itself',
          tile: [plain('years and Lifetime has '), strong('paid for itself')],
        };
      })();
  const keepEveryYear = {
    value: money(current.annualCents),
    label: temporary ? 'a year at your regular rate' : 'back in your pocket every year',
  };
  const stats: MemberOfferStat[] = sale
    ? [
        { value: `${percentOff(list, price)}% off`, label: `the ${money(list)} price` },
        keepEveryYear,
        { value: breakEven.value, label: breakEven.label },
      ]
    : [
        keepEveryYear,
        { value: breakEven.value, label: breakEven.label },
        // The five-year total is a wider number than a phone's stat cell; the ledger carries it.
        { value: money(0), label: 'every year after, forever' },
      ];

  const urgency = sale
    ? `The sale ends ${deadline}. Then Lifetime is ${money(list)} again.`
    : periodEnds
    ? `Your next ${money(current.periodCents)} ${payment} on ${memberRenewalDay(
        periodEnds,
      )} stays in your pocket. So does every one after it.`
    : `Every ${money(current.periodCents)} ${payment}${regular} stays in your pocket. Forever.`;

  const credit = quote.adjustmentCents < 0 ? -quote.adjustmentCents : 0;
  const chargeLine: CopyLine = [
    plain('One '),
    strong(money(due)),
    plain(' charge today on your card on file. '),
    ...(credit > 0 ? [plain(`Your ${money(credit)} account credit is applied. `)] : []),
    plain('Your plan stays active for life and never charges you again.'),
  ];

  const rows: MemberOfferReceiptRow[] = [
    {
      label: 'Lifetime access, one-time',
      detail: sale
        ? `Sale price, ${money(saleOff)} off until ${deadline}`
        : 'Your one-time Lifetime price',
      value: money(price),
    },
    ...(quote.adjustmentCents < 0
      ? [
          {
            label: 'Account credit',
            detail: 'Already on your account',
            value: `−${money(-quote.adjustmentCents)}`,
          },
        ]
      : quote.adjustmentCents > 0
      ? [
          {
            label: 'Account balance',
            detail: 'Owed on your account',
            value: money(quote.adjustmentCents),
          },
        ]
      : []),
  ];

  const tiles = sale
    ? [
        {
          value: money(saleOff),
          label: [plain('off the '), strong(money(list)), plain(' price')],
        },
        {
          value: money(current.annualCents),
          label: temporary
            ? [plain('a year at your '), strong('regular rate')]
            : [plain('back in your pocket '), strong('every year')],
        },
        { value: breakEven.value, label: breakEven.tile },
        { value: money(0), label: [plain('every year after, '), strong('forever')] },
      ]
    : [
        {
          value: money(current.annualCents),
          label: temporary
            ? [plain('a year at your '), strong('regular rate')]
            : [plain('back in your pocket '), strong('every year')],
        },
        { value: breakEven.value, label: breakEven.tile },
        fiveYear > 0
          ? {
              value: money(fiveYear),
              label: [
                plain('kept over '),
                strong('five years'),
                plain(temporary ? ' at your regular rate' : ' vs renewing'),
              ],
            }
          : {
              value: money(current.periodCents),
              label: temporary
                ? [plain('your regular '), strong(`${unit}’s payment`)]
                : [plain('back every '), strong(unit)],
            },
        { value: money(0), label: [plain('every year after, '), strong('forever')] },
      ];

  const decline = regular
    ? `No thanks, I’ll stay ${adjective}`
    : `No thanks, I’ll keep paying ${currentPeriod}`;
  const cta = `Get Lifetime for ${money(price)}`;

  return {
    variant,
    card: {
      eyebrow: sale ? `Member sale · Lifetime ${money(saleOff)} off` : 'Lifetime · pay once',
      headline: 'Never pay for the Dashboard again.',
      price: sale
        ? { now: money(price), unit: ' once', was: money(list), wasLabel: `after ${deadline}` }
        : {
            now: money(price),
            unit: ' once',
            was: `${money(current.periodCents)}${SHORT_UNIT[term]}`,
            wasLabel: regular ? 'your regular rate' : `yours to keep, every ${unit}`,
          },
      priceCaption: [
        strong('One payment.'),
        plain(' The last one you’ll ever make for the Dashboard.'),
      ],
      stats,
      urgency,
      checkboxLabel: `Yes! Lock in Lifetime. ${money(due)} today, my last Dashboard payment.`,
      chargeLine,
      ctaLabel: cta,
      ctaSubmittingLabel: 'Unlocking Lifetime…',
      declineLabel: decline,
    },
    receipt: {
      title: 'The last Dashboard payment you’ll ever make.',
      rows,
      total: { label: 'Due today', value: money(due) },
      // On a phone the second sentence takes its own line rather than leaving "is $0." alone.
      footnote: [plain('Lifetime starts today. '), whole('Every future Dashboard renewal is $0.')],
    },
    ledger: {
      title: temporary
        ? `${money(due)} today. Leave regular ${currentPeriod} payments behind.`
        : `${money(due)} today. Then ${money(
            current.annualCents,
          )} back in your pocket, every year.`,
      tiles,
      bars: [
        {
          amountCents: current.annualCents * 5,
          label: temporary
            ? `Your ${adjective} plan at its regular rate, five years`
            : `Your ${adjective} plan, five years`,
          value: money(current.annualCents * 5),
        },
        { amountCents: price, isYours: true, label: 'Lifetime, one payment', value: money(price) },
      ],
      barsLabel: 'Five years of the Dashboard, compared',
    },
    included: {
      title: 'Everything we ever build. Free, for life.',
      lines: DASHBOARD_LIFETIME_INCLUDED_LINES,
    },
    proof: DASHBOARD_LIFETIME_OFFER_QUOTE,
    bar: {
      price: money(price),
      detail: 'once, for life',
      cta: 'Get Lifetime',
      decline: 'No thanks',
    },
    success: {
      title: 'Welcome to Lifetime.',
      body: periodEnds
        ? `The Dashboard is yours for life. Your next ${money(
            current.periodCents,
          )} ${payment} on ${memberRenewalDay(
            periodEnds,
          )} stays in your pocket, and so does every one after it.`
        : `The Dashboard is yours for life. Every ${money(
            current.periodCents,
          )} ${payment}${regular} stays in your pocket.`,
    },
    dialog: sale
      ? {
          title: `Leave without ${money(saleOff)} off Lifetime?`,
          body: `Lifetime is ${money(price)} until ${deadline}, then ${money(list)}. ${
            temporary
              ? `Your regular ${currentPeriod} comes back after ${memberRenewalDay(
                  quote.currentDiscountEndsAt!,
                )}.`
              : `Your ${money(current.periodCents)} ${payment} keeps charging every ${unit}.`
          }`,
          keepLabel: cta,
          leaveLabel: temporary
            ? `Back to trading on ${adjective}`
            : dialogLeave(money, current.periodCents, term),
        }
      : null,
  };
};

/** The whole Dashboard offer for one quote. `currentTerm` is the plan the member is on today. */
export const getMemberOfferCopy = ({
  quote,
  currentTerm,
  fromEmail = false,
}: {
  quote: MemberOfferQuote;
  currentTerm: SaleTerm;
  fromEmail?: boolean;
}): MemberOfferCopy => {
  const term = sourceTermOf(currentTerm);
  const variant = getMemberOfferVariant(quote, fromEmail);
  return quote.term === SaleTerm.Lifetime
    ? getLifetimeCopy(quote, term, variant)
    : getAnnualCopy(quote, term, variant);
};

/** The plan tabs: each carries its price, so Lifetime's is visible before anyone presses it. */
export const getMemberTermTab = (
  row: MemberOfferSubscription,
  term: MemberOfferTerm,
): { label: string; detail: string; offer: string; onSale: boolean } => {
  const money = (cents: number) => memberMoney(cents, row.currency || 'usd');
  if (term === SaleTerm.Lifetime) {
    const lifetime = row.lifetime;
    return {
      label: 'Lifetime',
      detail: lifetime ? `${money(lifetime.priceCents)} once` : 'Pay once',
      offer: lifetime?.sale
        ? `Save ${money(lifetime.listCents - lifetime.priceCents)}`
        : 'Yours for life',
      onSale: !!lifetime?.sale,
    };
  }
  const annual = row.annual;
  if (!annual) return { label: 'Annual', detail: 'Pay yearly', offer: '', onSale: false };
  const off =
    annual.percentOff !== null
      ? `${formatDiscountPercent(annual.percentOff)}% off`
      : `${money(annual.listCents - annual.firstTermCents)} off`;
  return {
    label: 'Annual',
    detail: `${money(Math.round(annual.firstTermCents / 12))}/mo`,
    offer: `${off} ${annual.duration === 'forever' ? 'forever' : 'first year'}`,
    onSale: !!row.sale,
  };
};

/** The headline above the offer, with the actual sale terms before its countdown. */
export const getMemberSaleHeadline = (quote: MemberOfferQuote) => {
  if (quote.sale)
    return `${formatDiscountPercent(quote.sale.percentOff)}% off Annual ${
      quote.sale.duration === 'forever' ? 'forever' : 'this year'
    }`;
  if (quote.lifetimeSale)
    return `Save ${memberMoney(
      quote.lifetimeSale.listCents - quote.lifetimeSale.priceCents,
      quote.currency,
    )} on Lifetime`;
  return '';
};

export interface MemberOfferTeaser {
  eyebrow: string;
  line: string;
  cta: string;
  /** The sidebar's hover label. */
  tooltip: string;
  /** The plan tab the pitch is about, so its button opens that offer. */
  term?: MemberOfferTerm;
}

/**
 * The one-line pitch for the Settings card and the sidebar, from discovery's numbers. A sale leads
 * with the sale; otherwise the line names both rungs with their prices.
 */
export const getMemberOfferTeaser = (row: MemberOfferSubscription): MemberOfferTeaser => {
  const money = (cents: number) => memberMoney(cents, row.currency || 'usd');
  const annual = row.annual;
  const lifetime = row.lifetime;
  const sale = row.sale;
  const percent = (value: number | null) =>
    value !== null ? `${formatDiscountPercent(value)}% off` : 'Sale price on';
  const forever = annual?.duration === 'forever';
  const annualOff = annual
    ? annual.percentOff !== null
      ? `${formatDiscountPercent(annual.percentOff)}% off`
      : `${money(annual.listCents - annual.firstTermCents)} off`
    : '';
  const foreverPitch = annual
    ? `${annual.keepsExisting ? 'Keep your' : 'Lock in'} ${annualOff} on annual, forever. ${money(
        Math.round(annual.firstTermCents / 12),
      )}/mo.`
    : '';
  // What the annual offer keeps in the member's pocket against the plan they pay for today.
  const saving =
    annual && row.currentAnnualCents && !row.currentDiscountEndsAt
      ? row.currentAnnualCents - annual.firstTermCents
      : 0;
  const keeps = saving > 0 ? ` Save ${money(saving)} ${forever ? 'every year' : 'this year'}.` : '';
  if (sale && annual && !annual.keepsExisting)
    return {
      eyebrow: sale.title,
      line: `${percent(sale.percentOff)} annual${
        sale.duration === 'forever' ? ', forever' : ', your first year'
      }.${keeps} Ends ${memberDeadline(sale.endsAt)}.`,
      cta: 'See the sale',
      tooltip: `${percent(sale.percentOff)} annual`,
      term: SaleTerm.Annual,
    };
  if (lifetime?.sale)
    return {
      eyebrow: lifetime.sale.title,
      line: `Lifetime for ${money(lifetime.priceCents)}, ${money(
        lifetime.listCents - lifetime.priceCents,
      )} off. Ends ${memberDeadline(lifetime.sale.endsAt)}.`,
      cta: 'See the sale',
      tooltip: `Lifetime ${money(lifetime.priceCents)}`,
      term: SaleTerm.Lifetime,
    };
  const lifetimeLine = lifetime
    ? ` Or pay once for Lifetime at ${money(lifetime.priceCents)}.`
    : '';
  if (annual && saving > 0)
    return {
      eyebrow: 'Your member offers',
      line: forever
        ? `${foreverPitch}${keeps}`
        : `Go annual and save ${money(saving)} this year.${lifetimeLine}`,
      cta: 'See my offers',
      tooltip: `Save ${money(saving)} on annual`,
      term: SaleTerm.Annual,
    };
  if (annual)
    return {
      eyebrow: 'Your member offers',
      line: forever
        ? `${foreverPitch}${lifetimeLine}`
        : `Annual at ${money(
            Math.round(annual.firstTermCents / 12),
          )}/mo your first year.${lifetimeLine}`,
      cta: 'See my offers',
      tooltip: `Annual at ${money(Math.round(annual.firstTermCents / 12))}/mo`,
      term: SaleTerm.Annual,
    };
  if (lifetime)
    return {
      eyebrow: 'Your member offers',
      line: `Pay once for Lifetime at ${money(
        lifetime.priceCents,
      )}. Never pay for the Dashboard again.`,
      cta: 'See my offers',
      tooltip: `Lifetime for ${money(lifetime.priceCents)}`,
      term: SaleTerm.Lifetime,
    };
  return {
    eyebrow: 'Your member offers',
    line: 'See what your plan unlocks.',
    cta: 'See my offers',
    tooltip: 'Member offers',
  };
};

/** The amber line once a renewed price changes today's charge: the new amount, then the ask. */
export const getChargeMovedLine = (dueCents: number, currency: string) =>
  `Today’s charge is now ${memberMoney(dueCents, currency)}. Tick the box to confirm it.`;

/** The amber line when the tick is asked for again at the same charge. */
export const getChargeConfirmLine = (dueCents: number, currency: string) =>
  `Tick the box to confirm today’s ${memberMoney(dueCents, currency)} charge.`;

/** Which amber line the charge needs, if any: a new amount, or the same amount asked again. */
export type ChargeNotice = 'moved' | 'confirm' | null;
export const getChargeNoticeLine = (notice: ChargeNotice, dueCents: number, currency: string) =>
  notice === 'moved'
    ? getChargeMovedLine(dueCents, currency)
    : notice === 'confirm'
    ? getChargeConfirmLine(dueCents, currency)
    : '';

/** The line where the sale header stood, once a sale ends while its offer is open. */
export const getSaleEndedLine = (title: string) => `${title} ended. Here’s your offer today.`;

export interface MemberSaleStrip {
  title: string;
  /** The full line beside the countdown on wide screens. */
  line: string;
  /** The phone line, under the title. */
  short: string;
  cta: string;
}

/** The sale strip above the trading screen: the number in the sentence, the action on the button. */
export const getMemberSaleStrip = (row: MemberOfferSubscription): MemberSaleStrip | null => {
  const money = (cents: number) => memberMoney(cents, row.currency || 'usd');
  const sale = row.sale;
  const annual = row.annual;
  if (sale && annual) {
    const percent = `${formatDiscountPercent(sale.percentOff)}% off`;
    const scope = sale.duration === 'forever' ? 'annual, forever' : 'annual, your first year';
    const perMonth = money(Math.round(annual.firstTermCents / 12));
    // While a temporary discount runs, the regular rate is not what the member pays today.
    const current =
      row.currentAnnualCents !== undefined && !row.currentDiscountEndsAt
        ? ` instead of ${money(Math.round(row.currentAnnualCents / 12))}`
        : '';
    return {
      title: sale.title,
      line: `${percent} ${scope}. ${perMonth}/mo${current}.`,
      short: `${percent} ${scope}`,
      cta: `Get ${percent}`,
    };
  }
  const lifetime = row.lifetime;
  if (lifetime?.sale)
    return {
      title: lifetime.sale.title,
      line: `Save ${money(lifetime.listCents - lifetime.priceCents)} on Lifetime. ${money(
        lifetime.priceCents,
      )} once.`,
      short: `Save ${money(lifetime.listCents - lifetime.priceCents)} on Lifetime`,
      cta: 'Get Lifetime',
    };
  return null;
};

/** The modal with no offer to make: the plan's next step, in one line and one button. */
export const MEMBER_NEXT_ACTION_COPY = {
  pay_invoice: { title: 'Finish your payment. Then choose your offer.' },
  resume_plan: { title: 'Keep your plan. Keep your access.', label: 'Keep my plan' },
  resume_cancellation: { title: 'Pick up where you left off.', label: 'Continue my plan change' },
  trading: { title: 'You’re all set.', label: 'Back to trading' },
  verify: { title: 'Your plan is updating.', label: 'Reload offers' },
};

export interface MemberPricingPlan {
  term: SaleTerm.Monthly | SaleTerm.Quarterly | SaleTerm.Annual;
  name: string;
  perMonth: string;
  billed: string;
  saving: string | null;
  badge: string | null;
  cta: string;
}

/**
 * The modal for someone with no Dashboard plan: the three plans from the pricing page, at the
 * pricing page's prices, each one press from its checkout. Every plan is the same Dashboard; the
 * longer terms keep money in the member's pocket.
 */
export const getMemberPricingCopy = () => {
  const money = (cents: number) => memberMoney(cents, 'usd');
  const monthlyYear = DASHBOARD_REGULAR_MONTHLY_CENTS * 12;
  const quarterlySaving = monthlyYear - DASHBOARD_REGULAR_QUARTERLY_CENTS * 4;
  const annualSaving = monthlyYear - DASHBOARD_REGULAR_ANNUAL_CENTS;
  const plans: MemberPricingPlan[] = [
    {
      term: SaleTerm.Monthly,
      name: 'Monthly',
      perMonth: money(DASHBOARD_REGULAR_MONTHLY_CENTS),
      billed: `${money(DASHBOARD_REGULAR_MONTHLY_CENTS)} billed monthly`,
      saving: null,
      badge: null,
      cta: 'Start monthly',
    },
    {
      term: SaleTerm.Quarterly,
      name: 'Quarterly',
      perMonth: money(Math.round(DASHBOARD_REGULAR_QUARTERLY_CENTS / 3)),
      billed: `${money(DASHBOARD_REGULAR_QUARTERLY_CENTS)} every 3 months`,
      saving: `Save ${money(quarterlySaving)} a year`,
      badge: null,
      cta: 'Start quarterly',
    },
    {
      term: SaleTerm.Annual,
      name: 'Annual',
      perMonth: money(Math.round(DASHBOARD_REGULAR_ANNUAL_CENTS / 12)),
      billed: `${money(DASHBOARD_REGULAR_ANNUAL_CENTS)} a year`,
      saving: `Save ${money(annualSaving)} a year`,
      badge: 'Best value',
      cta: 'Start annual',
    },
  ];
  return {
    title: 'Dashboard plans',
    eyebrow: 'The full Dashboard',
    headline: 'Trade with the full Dashboard today.',
    body: `Every plan is the exact same Dashboard. Go annual and keep ${money(
      annualSaving,
    )} a year.`,
    plans,
    compare: 'Compare every feature',
  };
};

export interface WinbackCopy {
  tab: { label: string; detail: string; badge: string | null };
  card: MemberOfferCopy['card'];
  receipt: MemberOfferCopy['receipt'];
  bar: MemberOfferCopy['bar'];
  success: { title: string; body: string };
}

const WINBACK_NAME: Record<SourceTerm, string> = {
  [SaleTerm.Monthly]: 'Monthly',
  [SaleTerm.Quarterly]: 'Quarterly',
  [SaleTerm.Annual]: 'Annual',
};

/**
 * The come-back offer from the win-back email: 40% off the first payment of any term, then the
 * regular price. There is no deadline on it, so it has no clock and no decline dialog; the push is
 * the saving and the fact that everything the member saved is still there.
 */
export const getWinbackCopy = (
  quote: {
    term: SaleTerm;
    currency: string;
    firstTermCents: number;
    renewalCents: number;
    amountDueCents: number;
    taxCents: number;
    adjustmentCents: number;
  },
  annual?: { firstTermCents: number; renewalCents: number } | null,
): WinbackCopy => {
  const money = (cents: number) => memberMoney(cents, quote.currency);
  const term = sourceTermOf(quote.term);
  const unit = UNIT[term];
  const name = WINBACK_NAME[term];
  const first = quote.firstTermCents;
  const renewal = quote.renewalCents;
  const pct = percentOff(renewal, first);
  const saved = renewal - first;
  const months = term === SaleTerm.Annual ? 12 : term === SaleTerm.Quarterly ? 3 : 1;
  const annualSaved = annual ? annual.renewalCents - annual.firstTermCents : null;
  const third =
    term === SaleTerm.Monthly
      ? annualSaved && annualSaved > saved
        ? { value: money(annualSaved), label: 'off today if you pick annual' }
        : { value: money(first), label: 'for your first month' }
      : {
          value: money(Math.round(first / months)),
          label: `a month, your first ${unit}`,
        };
  const tax = quote.taxCents;
  return {
    tab: {
      label: name,
      detail: `${money(first)} first ${unit}`,
      badge: term === SaleTerm.Annual ? 'Best value' : null,
    },
    card: {
      eyebrow: `Welcome back · ${pct}% off your first payment`,
      headline: `Come back for ${pct}% off.`,
      price: {
        now: money(first),
        unit: ` first ${unit}`,
        was: `${money(renewal)}${SHORT_UNIT[term]}`,
        wasLabel: 'regular price',
      },
      priceCaption: [
        strong(`Then ${money(renewal)} a ${unit}.`),
        plain(' The full Dashboard, exactly as you left it.'),
      ],
      stats: [
        { value: `−${pct}%`, label: 'your first payment' },
        { value: money(saved), label: 'back in your pocket today' },
        third,
      ],
      urgency: 'Everything you saved is right where you left it.',
      checkboxLabel: `Yes! Come back at ${pct}% off. ${money(
        quote.amountDueCents,
      )} today, then ${money(renewal)} a ${unit}.`,
      chargeLine: [
        strong(
          tax > 0
            ? `${money(quote.amountDueCents)} today, tax included.`
            : `${money(quote.amountDueCents)} today.`,
        ),
        plain(` Then ${money(renewal)} a ${unit} until you cancel, any time, in Settings.`),
      ],
      ctaLabel: `Come back for ${money(first)}`,
      ctaSubmittingLabel: 'Restarting your plan…',
      declineLabel: 'No thanks',
    },
    receipt: {
      title: 'One payment. You’re back today.',
      rows: [
        {
          label: `Your first ${unit}`,
          detail: `${pct}% off the ${money(renewal)} regular price`,
          value: money(first),
        },
        ...(tax !== 0 ? [{ label: 'Tax', value: money(tax) }] : []),
        ...(quote.adjustmentCents < 0
          ? [
              {
                label: 'Account credit',
                detail: 'Already on your account',
                value: `−${money(-quote.adjustmentCents)}`,
              },
            ]
          : quote.adjustmentCents > 0
          ? [
              {
                label: 'Account balance',
                detail: 'Owed on your account',
                value: money(quote.adjustmentCents),
              },
            ]
          : []),
      ],
      total: { label: 'Due today', value: money(quote.amountDueCents) },
      footnote: [
        plain(`Renews at ${money(renewal)} a ${unit}, plus any tax. Cancel any time in Settings.`),
      ],
    },
    bar: {
      price: money(first),
      detail: `first ${unit}`,
      cta: 'Come back',
      decline: 'No thanks',
    },
    success: {
      title: 'You’re back.',
      body: `Your ${name.toLowerCase()} plan is live. The flow is waiting.`,
    },
  };
};
